import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { fetchArticle } from "@/lib/shopify";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { formatArticleHtml } from "@/lib/format-article-html";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

const articleQueryOptions = (blog: string, article: string) =>
  queryOptions({
    queryKey: ["shopify-article", blog, article],
    queryFn: () => fetchArticle(blog, article),
  });

export const Route = createFileRoute("/field-notes/$blog/$article")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(articleQueryOptions(params.blog, params.article)),
  head: ({ params, loaderData }) => {
    const a = loaderData as
      | { title?: string; excerpt?: string; image?: { url?: string }; publishedAt?: string; authorV2?: { name?: string } }
      | undefined;
    const title = a?.title ? `${a.title} — House of Apocrypha` : "Field Notes — House of Apocrypha";
    const desc = a?.excerpt ?? "A field note from House of Apocrypha.";
    const path = `https://houseofapocrypha.com/field-notes/${params.blog}/${params.article}`;
    const blogPath = `https://houseofapocrypha.com/field-notes/${params.blog}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        ...(a?.image?.url
          ? [
              { property: "og:image", content: a.image.url },
              { name: "twitter:image", content: a.image.url },
            ]
          : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: path },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a?.title ?? "Field Notes",
            description: desc,
            ...(a?.image?.url ? { image: a.image.url } : {}),
            ...(a?.publishedAt ? { datePublished: a.publishedAt } : {}),
            ...(a?.authorV2?.name ? { author: { "@type": "Person", name: a.authorV2.name } } : {}),
            mainEntityOfPage: path,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "Field Notes", item: "https://houseofapocrypha.com/field-notes" },
              { "@type": "ListItem", position: 3, name: ({"the-herbal-codex":"Natural Perfumery Primer","materia-botanica":"Materia Botanica","perfume-chapters":"Bottle Mythologies"} as Record<string,string>)[params.blog] ?? params.blog.replace(/-/g, " "), item: blogPath },
              { "@type": "ListItem", position: 4, name: a?.title ?? params.article, item: path },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="text-3xl text-parchment">This entry isn't in the archive</h1>
        <Link to="/field-notes" className="mt-8 inline-block eyebrow text-gold">
          Return to Field Notes
        </Link>
      </div>
    </div>
  ),
  component: ArticlePage,
});

const BLOG_LABELS: Record<string, string> = {
  "the-herbal-codex": "Natural Perfumery Primer",
  "materia-botanica": "Materia Botanica",
  "perfume-chapters": "Bottle Mythologies",
};

function ArticlePage() {
  const { blog, article } = Route.useParams();
  const { data } = useSuspenseQuery(articleQueryOptions(blog, article));
  if (!data) throw notFound();
  const blogLabel = BLOG_LABELS[blog] ?? blog.replace(/-/g, " ");

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <main className="mx-auto max-w-3xl px-6 py-20 lg:py-28">
        <nav className="text-sm">
          <Link to="/field-notes" className="text-muted-foreground hover:text-gold">
            Field Notes
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <Link
            to="/field-notes/$blog"
            params={{ blog }}
            className="text-muted-foreground hover:text-gold"
          >
            {blogLabel}
          </Link>
        </nav>


        <header className="mt-10 text-center">
          <h1 className="text-4xl text-parchment sm:text-5xl">{data.title}</h1>
          {data.authorV2?.name && (
            <p className="mt-4 font-serif italic text-muted-foreground">
              by {data.authorV2.name}
            </p>
          )}
          <div className="ornament mt-8">
            <span>✶</span>
          </div>
        </header>

        {data.image?.url && (
          <div className="mt-10 overflow-hidden border border-border">
            <ResponsiveImage
              src={data.image.url}
              alt={data.image.altText || `Illustration for the field note “${data.title}”`}
              sizes="(min-width: 768px) 720px, 100vw"
              priority
              pictureClassName="contents"
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
        )}

        <article
          className="field-note-prose mt-12"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(formatArticleHtml(data.contentHtml)) }}
        />

        <div className="mt-16 border-t border-border pt-8 text-center">
          <Link
            to="/field-notes/$blog"
            params={{ blog }}
            className="eyebrow text-gold hover:text-gold-soft"
          >
            ← More from this journal
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
